
import React, { useState, useEffect } from 'react';
import { Minus, Plus } from 'lucide-react';

interface SmartInputProps {
  label?: string;
  value: number;
  onChange: (val: number) => void;
  step?: number;
  min?: number;
  max?: number;
  subLabel?: string;
  description?: string | React.ReactNode;
  isCurrency?: boolean;
}

export const SmartNumberInput: React.FC<SmartInputProps> = ({
  label, value, onChange, step = 1, min = 0, max, subLabel, description, isCurrency = false
}) => {
  const [displayValue, setDisplayValue] = useState<string>(value.toString());

  useEffect(() => {
    setDisplayValue(value.toString());
  }, [value]);

  const handleBlur = () => {
    let num = parseFloat(displayValue);
    if (isNaN(num) || displayValue.trim() === '') {
      num = min;
    }

    // Clamp constraints
    if (num < min) num = min;
    if (max !== undefined && num > max) num = max;

    setDisplayValue(num.toString());
    onChange(num);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === '' || /^-?\d*\.?\d*$/.test(val)) {
      setDisplayValue(val);
      const parsed = parseFloat(val);
      if (!isNaN(parsed)) {
        onChange(parsed);
      }
    }
  };

  const handleStep = (direction: 1 | -1) => {
    const current = parseFloat(displayValue) || 0;
    let next = Number((current + (step * direction)).toFixed(2));

    if (next < min) next = min;
    if (max !== undefined && next > max) next = max;

    setDisplayValue(next.toString());
    onChange(next);
  };

  return (
    <div className="bg-card-bg p-3 rounded-xl border border-border-dim shadow-sm flex flex-col justify-between h-full">
      <div className="flex justify-between items-start mb-2">
        <div>
          {label && <label className="text-sm font-bold text-text-secondary uppercase tracking-wide block">{label}</label>}
          {subLabel && <span className="text-xs text-text-muted font-medium">{subLabel}</span>}
          {description && <div className="text-xs text-text-muted font-medium leading-tight mt-1">{description}</div>}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-auto">
        <button
          type="button"
          onClick={() => handleStep(-1)}
          aria-label="Diminuir valor"
          className="w-8 h-8 rounded-lg bg-input-bg text-text-primary flex items-center justify-center hover:bg-bg-main active:scale-95 transition-all border border-input-border"
        >
          <Minus className="w-4 h-4 text-icon-default" />
        </button>
        <div className="flex-1 relative">
          {isCurrency && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted font-mono text-sm z-10">R$</span>}
          <input
            type="text"
            inputMode="decimal"
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full bg-input-bg border border-input-border rounded-lg py-1.5 ${isCurrency ? 'pl-7' : 'pl-2'} pr-2 text-center font-mono font-bold text-lg text-text-primary focus:ring-1 focus:ring-sal-cyber-teal focus:border-sal-cyber-teal focus:outline-none transition-colors placeholder-text-muted`}
          />
        </div>
        <button
          type="button"
          onClick={() => handleStep(1)}
          aria-label="Aumentar valor"
          className="w-8 h-8 rounded-lg bg-sal-cyber-teal text-badge-text-inv flex items-center justify-center hover:brightness-110 active:scale-95 transition-all shadow-sm"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
